<template>
  <v-form ref="form" class="update-form">
    <v-container grid-list-lg fluid>
      <v-layout wrap>
        <template v-if="$project === 'hwy'">
          <v-flex xs12 py-0>
            <div class="font-weight-bold pb-1 pl-1">
              <RequiredDot>{{ $t('user.first_name') }}</RequiredDot>
            </div>
            <v-text-field
              v-model="formData.first_name"
              :rules="[rules.required, rules.noNumOnly]"
              solo
              clearable></v-text-field>
          </v-flex>
        </template>
        <template v-else>
          <v-flex xs6 sm6 py-0>
            <div class="font-weight-bold pb-1 pl-1">
              <RequiredDot>{{ $t('user.first_name') }}</RequiredDot>
            </div>
            <v-text-field
              v-model="formData.first_name"
              :rules="[rules.required, rules.noNumOnly]"
              solo
              clearable></v-text-field>
          </v-flex>
          <v-flex xs6 sm6 py-0>
            <div class="font-weight-bold pb-1 pl-1">
              <RequiredDot>{{ $t('user.last_name') }}</RequiredDot>
            </div>
            <v-text-field
              v-model="formData.last_name"
              :rules="[rules.required, rules.noNumOnly]"
              solo
              clearable></v-text-field>
          </v-flex>
        </template>
        <v-flex :xs12="!requiredOnly" :xs6="requiredOnly" sm6 py-0>
          <div class="font-weight-bold pb-1 pl-1">
            <RequiredDot>{{ $t('user.country_region') }}</RequiredDot>
          </div>
          <v-autocomplete
            v-model="formData.country"
            :items="countryList"
            @change="changeCountry"
            item-text="name"
            :rules="[rules.required]"
            solo
            hide-no-data></v-autocomplete>
        </v-flex>
        <v-flex
          v-if="!minimalForm || requiresState || additionalOpened"
          v-show="!minimalForm || requiresState || expanded"
          :class="{
            'update-form__additional': minimalForm && !requiresState,
            'update-form__minimum-state': minimalForm && requiresState && !expanded
          }"
          xs6
          sm6
          py-0>
          <div class="font-weight-bold pb-1 pl-1">
            <RequiredDot :required="!minimalForm || requiresState">
              {{ $t('user.state') }}
            </RequiredDot>
          </div>
          <v-autocomplete
            :disabled="disabledState || this.stateList.length === 0"
            v-model="formData.state"
            :items="stateList"
            item-text="name"
            :rules="!minimalForm || requiresState ? [rules.required] : []"
            solo
            hide-no-data></v-autocomplete>
        </v-flex>
        <v-flex
          v-if="!minimalForm || additionalOpened"
          v-show="!minimalForm || expanded"
          class="update-form__additional"
          xs6
          sm6
          py-0>
          <div class="font-weight-bold pb-1 pl-1">
            <RequiredDot :required="!minimalForm">{{ $t('user.city') }}</RequiredDot>
          </div>
          <v-text-field
            solo
            v-model="formData.city"
            :rules="minimalForm ? [rules.canNotHTMLTag] : [rules.required, rules.canNotHTMLTag]"
            clearable></v-text-field>
        </v-flex>
        <v-flex
          v-if="!minimalForm || additionalOpened"
          v-show="!minimalForm || expanded"
          class="update-form__additional"
          xs12
          sm6
          py-0>
          <div class="font-weight-bold pb-1 pl-1">
            <RequiredDot :required="!minimalForm">{{ $t('user.postcode') }}</RequiredDot>
          </div>
          <v-text-field
            v-model="formData.postcode"
            :rules="minimalForm ? [rules.canNotHTMLTag] : [rules.required, rules.canNotHTMLTag]"
            solo
            clearable></v-text-field>
        </v-flex>
        <v-flex
          v-if="!requiredOnly && (!minimalForm || additionalOpened)"
          v-show="!minimalForm || expanded"
          class="update-form__additional update-form__phone"
          xs12
          sm6
          py-0>
          <div class="font-weight-bold pb-1 pl-1">
            <RequiredDot :required="false">{{ $t('user.phone_number') }}</RequiredDot>
          </div>
          <NumberInput
            solo
            v-model="formData.phone_number"
            :rules="[rules.canNotHTMLTag]"></NumberInput>
        </v-flex>
        <v-flex
          v-if="!minimalForm || additionalOpened"
          v-show="!minimalForm || expanded"
          class="update-form__additional"
          xs12
          py-0>
          <div class="font-weight-bold pb-1 pl-1">
            <RequiredDot :required="!minimalForm">{{ $t('user.address') }}</RequiredDot>
          </div>
          <v-text-field
            v-model="formData.address1"
            :rules="minimalForm ? [rules.canNotHTMLTag] : [rules.required, rules.canNotHTMLTag]"
            solo
            clearable></v-text-field>
        </v-flex>
        <v-flex
          v-if="!requiredOnly && (!minimalForm || additionalOpened)"
          v-show="!minimalForm || expanded"
          class="update-form__additional"
          xs12
          py-0>
          <div class="font-weight-bold pb-1 pl-1">
            <RequiredDot :required="false">{{ $t('user.address_2') }}</RequiredDot>
          </div>
          <v-text-field
            v-model="formData.address2"
            :rules="[rules.canNotHTMLTag]"
            solo
            clearable></v-text-field>
        </v-flex>
        <v-flex
          v-if="!requiredOnly && (!minimalForm || additionalOpened)"
          v-show="!minimalForm || expanded"
          class="update-form__additional"
          xs12
          py-0>
          <div class="font-weight-bold pb-1 pl-1">
            <RequiredDot :required="false">{{ $t('user.company') }}</RequiredDot>
          </div>
          <v-text-field
            v-model="formData.company"
            solo
            clearable
            :rules="[rules.canNotHTMLTag]"></v-text-field>
        </v-flex>
      </v-layout>
    </v-container>
  </v-form>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import rules from '@/module/rules/index.js'
import RequiredDot from '@/components/RequiredDot.vue'
import NumberInput from '@/components/NumberInput/index.vue'

export default {
  name: 'UpdateForm',
  props: {
    minimal: { type: Boolean, default: false },
    expanded: { type: Boolean, default: false },
    requiredOnly: { type: Boolean, default: false }
  },
  data() {
    return {
      formData: {},
      // 固定本次表单模式，避免保存后状态变化导致完整字段在关闭前闪现。
      minimalForm: this.minimal && this.$store.state.client.clientInfo.status !== 'Uncertain',
      additionalOpened: this.expanded,
      countryList: [],
      stateList: [],
      disabledState: false,
      rules
    }
  },
  components: { RequiredDot, NumberInput },
  computed: {
    minimumFields() {
      const country = this.countryList.find((item) => item.name === this.formData.country)
      return rules.billingRequiredFields(this.$project, country)
    },
    requiresState() {
      return this.minimumFields.includes('state')
    },
    ...mapState('client', ['clientInfo'])
  },
  watch: {
    expanded(value) {
      if (value) this.additionalOpened = true
      if (value && this.formData.country && !this.requiresState) {
        this.getStates(this.formData.country)
      }
    }
  },
  methods: {
    changeCountry(countryName) {
      // 国家改变后不能携带上一个国家的州，避免提交不匹配的 billing 信息。
      this.formData.state = ''
      this.stateList = []
      this.getStates(countryName)
    },
    getStates(countryName) {
      if (this.minimalForm && !this.requiresState && !this.expanded) {
        this.stateList = []
        this.disabledState = false
        return
      }
      let country = this.countryList.find((o) => o.name === countryName)
      if (!country) {
        this.stateList = []
      } else {
        this.disabledState = true
        this.$http
          .get('/states/', {
            params: {
              country: country.id,
              page_size: 300
            }
          })
          .then((res) => {
            if (this.formData.country !== countryName) return
            this.disabledState = false
            this.stateList = res.results
            if (
              this.minimalForm &&
              !this.stateList.some((item) => item.name === this.formData.state)
            ) {
              this.formData.state = ''
            }
          })
          .catch((e) => {
            if (this.formData.country !== countryName) return
            this.disabledState = false
            this.$message.error(e.detail)
          })
      }
    },
    submit() {
      if (!this.$validate()) {
        return Promise.reject()
      }
      let data = this.formData
      if (this.minimalForm) {
        data = {}
        const fields = this.additionalOpened
          ? this.minimumFields.concat([
              'state',
              'city',
              'postcode',
              'phone_number',
              'address1',
              'address2',
              'company'
            ])
          : this.minimumFields
        // 仍以最小规则验单，展开的附加资料一并 PATCH，不提交账号只读字段。
        fields.forEach((field) => {
          if (this.formData[field] !== undefined) data[field] = this.formData[field]
        })
      }
      return this.$http({
        method: this.minimalForm ? 'patch' : 'put',
        url: `/clients/${this.clientInfo.id}/`,
        data
      })
        .then((res) => {
          this.setClientInfo(res)
          this.$emit('submit', res)
          return Promise.resolve(res)
        })
        .catch((e) => {
          this.$message.error(e.detail)
          return Promise.reject(e)
        })
    },
    $validate() {
      const valid = this.$refs.form.validate()
      // 附加字段保留挂载以校验已填内容；出错时展开，避免错误藏在收起区域。
      if (!valid && this.minimalForm && this.additionalOpened && !this.expanded) {
        this.$emit('expand')
      }
      // 州列表加载完成且选项属于当前国家后才允许提交。
      return (
        valid &&
        (!this.minimalForm ||
          (this.countryList.some((item) => item.name === this.formData.country) &&
            (!this.requiresState ||
              (!this.disabledState &&
                this.stateList.some((item) => item.name === this.formData.state)))))
      )
    },
    $reset() {
      this.$refs.form.reset()
    },
    init() {
      this.formData = Object.assign({}, this.clientInfo)
    },
    ...mapActions('client', ['getCountries']),
    ...mapMutations('client', ['setClientInfo'])
  },
  created() {
    this.init()
    this.getCountries().then((res) => {
      this.countryList = res
      if (this.clientInfo.country) {
        this.getStates(this.clientInfo.country)
      }
    })
  }
}
</script>

<style lang="scss">
.update-form {
  .v-input__control {
    min-height: 42px !important;
  }
  .v-input__slot {
    margin-bottom: 2px;
    box-shadow: none !important;
    border: 1px solid transparent;
    background-color: #eee !important;
    border-radius: 10px !important;
  }
  .v-text-field__details {
    margin-bottom: 0 !important;
  }
  .error--text {
    .v-input__slot {
      border: 1px solid #ff5252 !important;
    }
  }
}
</style>
